import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { KycType } from "../../../api/enrichedStakeholder";
import {
  CIN,
  DocumentEntityType,
  identityFormType,
  IdentityDocumentType,
  IdentityVerification,
  IdentityVerificationRow,
  ParentEntityType,
} from "../../../api/verificationsV2";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import BaseDataTable, { IDataRow } from "../../../components/ui/baseDataTable";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import {
  SaveFooter,
  VerificationEmptyTableBody,
  VerificationTableSection,
} from "../../legalTechnicalAnalysis/components/VerificationContainer";
import { selectFormConfigEntities } from "../../legalTechnicalAnalysis/Logic/formConfigsSlice";
import {
  getStakeholderBusinessDetails,
  getStakeholderKycDetails,
} from "../../stakeholder/logic/stakeholderSlice";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";
import {
  selectIdentityVerifications,
  selectIdentityVerificationsError,
  selectIdentityVerificationsStatus,
} from "../Logic/verificationsSlice";
import {
  fetchIdentityVerifications,
  upsertIdentityVerifications,
} from "../Logic/verificationsThunk";

import { buildIdentityVerificationColumns } from "./identityVerificationColumns";

interface IdentityVerificationsTableProps {
  parentEntityType: ParentEntityType;
  parentEntityId: string;
  stakeholderId: string;
}


const buildEmptyRow = (
  parentEntityType: ParentEntityType,
  parentEntityId: string,
  stakeholderId: string,
): IdentityVerificationRow => ({
  verification_id: uuidv4(),
  parent_entity_type: parentEntityType,
  parent_entity_id: parentEntityId,
  stakeholder_id: stakeholderId,
  document_type: undefined as unknown as IdentityDocumentType,
  document_entity_type: DocumentEntityType.STAKEHOLDER_KYC_DETAILS,
  document_entity_id: "",
  onboarding_details: {},
  current_details: {},
  anomaly: undefined,
  isAdded: true,
  isDirty: true,
});

const IdentityVerificationsTable: React.FC<IdentityVerificationsTableProps> = ({
  parentEntityType,
  parentEntityId,
  stakeholderId,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const status = useAppSelector(selectIdentityVerificationsStatus);
  const error = useAppSelector(selectIdentityVerificationsError);
  const formConfigs = useAppSelector(selectFormConfigEntities);

  const savedRows = useAppSelector(
    (state) =>
      selectIdentityVerifications(
        state,
        parentEntityType,
        parentEntityId,
        stakeholderId,
      ),
    shallowEqual,
  );

  const kycDetails = useAppSelector((state) =>
    getStakeholderKycDetails(state, stakeholderId),
  );
  const businessDetails = useAppSelector((state) =>
    getStakeholderBusinessDetails(state, stakeholderId),
  );

  const [rows, setRows] = useState<IdentityVerificationRow[]>([]);

  useEffect(() => {
    setRows(savedRows.map((row) => ({ ...row, isAdded: false, isDirty: false })));
  }, [savedRows, parentEntityId, stakeholderId]);

  useEffect(() => {
    if (!caseId || !stakeholderId) return;

    dispatch(
      fetchEnrichedStakeholder({ caseId, stakeholderIds: [stakeholderId] }),
    );
  }, [caseId, stakeholderId, dispatch]);

  const findSavedKycDetail = (kycType: KycType) =>
    kycDetails.find((detail) => detail.kyc_type === kycType);

  const resolveDocumentId = (documentType?: IdentityDocumentType) => {
    if (!documentType) return undefined;
    if (documentType === CIN) return businessDetails?.cin || undefined;

    return findSavedKycDetail(documentType)?.kyc_value || undefined;
  };

  const hasChanges = useMemo(() => rows.some((row) => row.isDirty), [rows]);

  const isStatusFormComplete = (
    statusValues: Record<string, any> | null | undefined,
    documentType: IdentityDocumentType,
  ) => {
    const mandatoryFields = (
      formConfigs[identityFormType(documentType)]?.configs?.fields ?? []
    ).filter((field) => field.is_mandatory);

    return mandatoryFields.every((field) => {
      const value = (statusValues ?? {})[field.key];

      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    });
  };

  const isRowReadyToSave = (row: IdentityVerificationRow) =>
    Boolean(row.document_type) &&
    Boolean(resolveDocumentId(row.document_type)) &&
    isStatusFormComplete(row.onboarding_details, row.document_type) &&
    isStatusFormComplete(row.current_details, row.document_type);

  const hasIncompleteRows = useMemo(
    () => rows.filter((row) => row.isDirty).some((row) => !isRowReadyToSave(row)),
    [rows, formConfigs, kycDetails, businessDetails],
  );

  const handleAddRow = () => {
    setRows((previous) => [
      ...previous,
      buildEmptyRow(parentEntityType, parentEntityId, stakeholderId),
    ]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setRows((previous) => previous.filter((_, index) => index !== rowIndex));
  };

  const handleRowChange = (
    rowIndex: number,
    value: any,
    field: keyof IdentityVerificationRow,
  ) => {
    setRows((previous) =>
      previous.map((row, index) => {
        if (index !== rowIndex) return row;

        if (field === "document_type") {
          return {
            ...row,
            document_type: value,
            onboarding_details: {},
            current_details: {},
            anomaly: undefined,
            isDirty: true,
          };
        }

        return { ...row, [field]: value, isDirty: true };
      }),
    );
  };

  const handleSave = async () => {
    if (!caseId) return;

    const dirtyRows = rows.filter((row) => row.isDirty);
    if (dirtyRows.length === 0) return;

    setIsSaving(true);

    try {
      const verifications: IdentityVerification[] = dirtyRows.map(
        ({ isAdded, isDirty, ...row }) => {
          return {
            ...row,
            anomaly: row.anomaly || null,
            document_entity_type:
              row.document_type === CIN
                ? DocumentEntityType.STAKEHOLDER_BUSINESS_DETAILS
                : DocumentEntityType.STAKEHOLDER_KYC_DETAILS,
            document_entity_id:
              row.document_type === CIN
                ? stakeholderId
                : (findSavedKycDetail(row.document_type)?.kyc_id ?? ""),
          };
        },
      );

      await dispatch(upsertIdentityVerifications({ caseId, verifications })).unwrap();

      showSuccessToast("Identity verifications saved successfully");
    } catch {
      showErrorToast("Failed to save verifications. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setRows(savedRows.map((row) => ({ ...row, isAdded: false, isDirty: false })));
  };

  const handleRetry = () => {
    if (caseId) dispatch(fetchIdentityVerifications(caseId));
  };

  const dataRows: IDataRow<IdentityVerificationRow>[] = useMemo(
    () =>
      rows.map((row) => ({
        data: row,
        isEditable: true,
        widgets: row.isAdded ? ["add", "delete"] : ["add"],
        onAdd: handleAddRow,
        onDelete: handleDeleteRow,
      })),
    [rows],
  );

  const isLoading = status === "loading" && !isSaving;

  return (
    <div className="flex w-full flex-col">
      <VerificationTableSection
        title="Identity Verifications"
      >
        {isLoading ? (
          <CustomLoadingSpinner
            title="Loading Verifications..."
            subtitle="Fetching identity verifications for this stakeholder."
            minHeight="min-h-[160px]"
          />
        ) : error ? (
          <ErrorDisplay
            error="Unable to load identity verifications. Please try again."
            onRetry={handleRetry}
          />
        ) : (
          <BaseDataTable<IdentityVerificationRow>
            columnConfigs={buildIdentityVerificationColumns({
              rows,
              stakeholderId,
              resolveDocumentId,
              onRowChange: handleRowChange,
            })}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <VerificationEmptyTableBody
                title="No identity verification records"
                subtitle="Add a document to record its onboarding and current status"
                onAdd={handleAddRow}
              />
            }
          />
        )}
      </VerificationTableSection>

      {hasChanges && (
        <SaveFooter
          isSaving={isSaving}
          disabled={hasIncompleteRows}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
        />
      )}
    </div>
  );
};

export default IdentityVerificationsTable;
